/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import * as React from 'react';

export interface UseResizableOptions {
  baseWidth: number;
  currentWidth: number | null;
  onWidthChange?: (width: number) => void;
  minWidth?: number;
  maxWidthFraction?: number;
}

export interface UseResizableResult {
  isDragging: boolean;
  handleResizeMouseDown: (e: React.MouseEvent) => void;
}

const DEFAULT_MIN_WIDTH = 250;
const DEFAULT_MAX_WIDTH_FRACTION = 0.9;

function useResizable({
  baseWidth,
  currentWidth,
  onWidthChange,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidthFraction = DEFAULT_MAX_WIDTH_FRACTION,
}: UseResizableOptions): UseResizableResult {
  const [isDragging, setIsDragging] = React.useState(false);
  const onWidthChangeRef = React.useRef(onWidthChange);
  onWidthChangeRef.current = onWidthChange;
  const dragCleanupRef = React.useRef<(() => void) | null>(null);

  React.useEffect(
    () => () => {
      dragCleanupRef.current?.();
    },
    []
  );

  const handleResizeMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = currentWidth ?? baseWidth;
      setIsDragging(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = startX - moveEvent.clientX;
        const maxWidth = window.innerWidth * maxWidthFraction;
        const newWidth = Math.max(minWidth, Math.min(startWidth + delta, maxWidth));
        onWidthChangeRef.current?.(newWidth);
      };

      const cleanup = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        dragCleanupRef.current = null;
      };

      const handleMouseUp = () => cleanup();
      dragCleanupRef.current = cleanup;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [baseWidth, currentWidth, minWidth, maxWidthFraction]
  );

  return { isDragging, handleResizeMouseDown };
}

export default useResizable;
